import { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus,
  Edit,
  Trash2,
  FileUp,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { standardsAPI } from '@/api/standards';
import { useRef } from 'react';

interface Standard {
  id: string;
  text: string;
  department: string;
  touchpoint: string;
  subtouchpoint: string;
  title: string;
}

interface StandardsTableProps {
  standards: Standard[];
  totalStandards?: number; // Made optional since not used internally
  onEditStandard?: (standard: Standard) => void;
  onDeleteStandard?: (standardId: string) => void;
  onColumnInfo: (columnType: string) => void;
  onImportCsv?: (file: File) => void;
  propertyType: string;
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
  isLoading?: boolean;
}

export function StandardsTable({
  standards,
  onEditStandard,
  onDeleteStandard,
  onColumnInfo,
  propertyType,
  currentPage: externalCurrentPage,
  totalPages: externalTotalPages,
  totalItems: externalTotalItems,
  onPageChange: externalPageChange,
  isLoading = false,
}: StandardsTableProps) {
  const [standardToDelete, setStandardToDelete] = useState<Standard | null>(
    null
  );
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const itemsPerPage = 10;
  const navigate = useNavigate();
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
    ];

    if (
      !validTypes.includes(file.type) &&
      !file.name.match(/\.(xlsx|xls|csv)$/)
    ) {
      toast({
        title: t('templates.table.invalidFile'),
        variant: 'destructive',
      });
      return;
    }

    setImporting(true);
    try {
      const response = await standardsAPI.importExcel(Number(id), file);

      toast({
        title: t('templates.table.importDialog.importSuccess'),
        description: t('templates.table.importDialog.importedCount', { count: response.data?.imported_count || 0 }),
      });

      // Refresh the page to show new standards
      setTimeout(() => window.location.reload(), 1500);
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.errors?.[0] || error.message || 'Import failed';
      toast({
        title: t('templates.table.importDialog.importFailed'),
        description: errorMsg,
        variant: 'destructive',
      });
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Use external pagination if provided, otherwise use internal
  const currentPage =
    externalCurrentPage !== undefined
      ? externalCurrentPage
      : internalCurrentPage;
  const totalPages =
    externalTotalPages !== undefined
      ? externalTotalPages
      : Math.ceil(standards.length / itemsPerPage);
  const handlePageChange = externalPageChange || setInternalCurrentPage;

  // For server-side pagination (when external pagination is provided), use standards as-is
  // For client-side pagination (when no external pagination), slice the array
  const currentStandards =
    externalCurrentPage !== undefined
      ? standards
      : standards.slice(
          (currentPage - 1) * itemsPerPage,
          currentPage * itemsPerPage
        );

  const handleEditClick = (standard: Standard) => {
    if (onEditStandard) {
      onEditStandard(standard);
    } else {
      // Navigate to edit-standards page with standard ID only
      navigate(`/admin/templates/${id}/edit-standards`, {
        state: { standardId: standard.id },
      });
    }
  };



  return (
    <div className={cn('space-y-3 sm:space-y-4 lg:space-y-6', isRTL && 'rtl')}>
      {/* Table Header */}
      <Card>
        <CardHeader className="p-3 sm:p-4 lg:p-6">
          <div
            className={cn(
              'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4',
              isRTL ? 'sm:flex-row-reverse' : 'sm:flex-row'
            )}
          >
            <div className={cn(isRTL && 'text-right')}>
              <CardTitle className="text-lg sm:text-xl font-bold truncate">
                <span className="hidden sm:inline">
                  {propertyType} {t('templates.table.tableTitle')}
                </span>
                <span className="sm:hidden">{propertyType} {t('templates.table.tableTitleMobile')}</span>
              </CardTitle>
              {externalCurrentPage !== undefined &&
              externalTotalPages !== undefined ? (
                // Server-side pagination - show page info and total items
                <p className="text-muted-foreground text-xs sm:text-sm">
                  <span className="hidden sm:inline">
                    {t('templates.table.page')} {currentPage}{' '}
                    {t('templates.table.of')} {totalPages}
                    {externalTotalItems &&
                      ` • ${externalTotalItems} ${t('templates.table.standards')}`}
                  </span>
                  <span className="sm:hidden">
                    {t('templates.table.page')} {currentPage} {t('templates.table.of')} {totalPages}
                    {externalTotalItems && ` • ${externalTotalItems} ${t('templates.table.standards')}`}
                  </span>
                </p>
              ) : (
                // Client-side pagination - show item range
                <p className="text-muted-foreground text-xs sm:text-sm">
                  <span className="hidden sm:inline">
                    {t('templates.table.showing')}{' '}
                    {(currentPage - 1) * itemsPerPage + 1}-
                    {Math.min(currentPage * itemsPerPage, standards.length)}{' '}
                    {t('templates.table.of')} {standards.length}{' '}
                    {t('templates.table.standards')}
                  </span>
                  <span className="sm:hidden">
                    {(currentPage - 1) * itemsPerPage + 1}-
                    {Math.min(currentPage * itemsPerPage, standards.length)} {t('templates.table.of')}{' '}
                    {standards.length}
                  </span>
                </p>
              )}
            </div>
            <div
              className={cn(
                'flex flex-col xs:flex-row items-stretch xs:items-center gap-2 sm:gap-3 w-full sm:w-auto',
                isRTL ? 'xs:flex-row-reverse' : 'xs:flex-row'
              )}
            >
              {onDeleteStandard && (
                <Button
                  onClick={() => navigate(`/admin/templates/${id}/add-standards`)}
                  size="sm"
                  className="w-full xs:w-auto text-xs sm:text-sm"
                >
                  <Plus
                    className={cn(
                      'w-3 h-3 sm:w-4 sm:h-4',
                      isRTL ? 'ml-1 sm:ml-2' : 'mr-1 sm:mr-2'
                    )}
                  />
                  <span className="hidden sm:inline">
                    {t('templates.table.addStandard')}
                  </span>
                  <span className="sm:hidden">{t('templates.table.addStandardMobile')}</span>
                </Button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="hidden"
              />
              {onDeleteStandard && (
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  size="sm"
                  className="w-full xs:w-auto text-xs sm:text-sm"
                  disabled={importing}
                >
                  <FileUp
                    className={cn(
                      'w-3 h-3 sm:w-4 sm:h-4',
                      isRTL ? 'ml-1 sm:ml-2' : 'mr-1 sm:mr-2'
                    )}
                  />
                  <span className="hidden sm:inline">
                    {importing
                      ? t('common.loading', 'Importing...')
                      : t('templates.table.importExcel')}
                  </span>
                  <span className="sm:hidden">
                    {importing ? t('common.loading', 'Importing...') : t('templates.table.importExcelMobile')}
                  </span>
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Standards Table */}
      <Card className="relative">
        <CardContent className="p-0">
          {/* Loading Overlay - only covers table */}
          {isLoading && (
            <div className="absolute inset-0 bg-background/60 backdrop-blur-[1px] z-10 flex items-center justify-center min-h-[200px]">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                <p className="text-sm text-muted-foreground">
                  {t('common.loading', 'Loading...')}
                </p>
              </div>
            </div>
          )}
          <div className={cn('overflow-x-auto', isRTL && 'dir-rtl')}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={cn('w-[150px]', isRTL && 'text-right')}>
                    <div
                      className={cn(
                        'flex items-center gap-2',
                        isRTL ? 'flex-row-reverse' : 'flex-row'
                      )}
                    >
                      {t('templates.table.department')}
                      {onEditStandard && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onColumnInfo('department')}
                          className="p-1 h-auto"
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className={cn('w-[150px]', isRTL && 'text-right')}>
                    <div
                      className={cn(
                        'flex items-center gap-2',
                        isRTL ? 'flex-row-reverse' : 'flex-row'
                      )}
                    >
                      {t('templates.table.touchpoint')}
                      {onEditStandard && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onColumnInfo('touchpoint')}
                          className="p-1 h-auto"
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className={cn('w-[120px]', isRTL && 'text-right')}>
                    <div
                      className={cn(
                        'flex items-center gap-2 text-nowrap',
                        isRTL ? 'flex-row-reverse' : 'flex-row'
                      )}
                    >
                      {t('templates.table.subTouchpoint')}
                      {onEditStandard && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onColumnInfo('subtouchpoint')}
                          className="p-1 h-auto"
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className={cn('w-[150px]', isRTL && 'text-right')}>
                    <div
                      className={cn(
                        'flex items-center gap-2',
                        isRTL ? 'flex-row-reverse' : 'flex-row'
                      )}
                    >
                      {t('templates.table.titleColumn')}
                      {onEditStandard && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onColumnInfo('title')}
                          className="p-1 h-auto"
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className={cn(isRTL && 'text-right')}>
                    {t('templates.table.standard')}
                  </TableHead>
                  {(onEditStandard || onDeleteStandard) && (
                    <TableHead className={cn('w-[120px]', isRTL && 'text-right')}>
                      {t('templates.table.actions')}
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentStandards.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={(onEditStandard || onDeleteStandard) ? 6 : 5}
                      className={cn(
                        'text-center py-8 text-muted-foreground',
                        isRTL && 'text-right'
                      )}
                    >
                      {t('templates.table.noStandards')}
                    </TableCell>
                  </TableRow>
                ) : (
                  currentStandards.map(standard => (
                    <TableRow key={standard.id}>
                      <TableCell
                        className={cn('font-medium', isRTL && 'text-right')}
                      >
                        {standard.department}
                      </TableCell>
                      <TableCell className={cn(isRTL && 'text-right')}>
                        {standard.touchpoint}
                      </TableCell>
                      <TableCell className={cn(isRTL && 'text-right')}>
                        {standard.subtouchpoint}
                      </TableCell>
                      <TableCell className={cn(isRTL && 'text-right')}>
                        {standard.title}
                      </TableCell>
                      <TableCell className={cn(isRTL && 'text-right')}>
                        <div className="max-w-md">
                          <p className="text-sm " title={standard.text}>
                            {standard.text.length > 30
                              ? `${standard.text.substring(0, 30)}...`
                              : standard.text}
                          </p>
                        </div>
                      </TableCell>
                      {(onEditStandard || onDeleteStandard) && (
                        <TableCell className={cn(isRTL && 'text-right')}>
                          <div
                            className={cn(
                              'flex items-center gap-2',
                              isRTL ? 'flex-row-reverse' : 'flex-row'
                            )}
                          >
                            {onEditStandard && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditClick(standard)}
                                title={t('templates.table.edit')}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            {onDeleteStandard && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setStandardToDelete(standard)}
                                className="text-destructive hover:text-destructive"
                                title={t('templates.table.delete')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination Controls */}
      {standards.length > 0 && (
        <Card>
          <CardContent className="py-2 sm:py-3 lg:py-4 px-3 sm:px-4 lg:px-6">
            <div className="flex items-center justify-center">
              <div
                className={cn(
                  'flex items-center gap-1 sm:gap-2',
                  isRTL ? 'flex-row-reverse' : 'flex-row'
                )}
              >
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm"
                >
                  {isRTL ? (
                    <>
                      <span className="hidden sm:inline">
                        {t('templates.table.previous')}
                      </span>
                      <span className="sm:hidden">{t('templates.table.previous')}</span>
                      <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                    </>
                  ) : (
                    <>
                      <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      <span className="hidden sm:inline">
                        {t('templates.table.previous')}
                      </span>
                      <span className="sm:hidden">{t('templates.table.previous')}</span>
                    </>
                  )}
                </Button>

                {/* Page numbers - Show fewer on mobile */}
                <div className="flex items-center gap-1">
                  {(() => {
                    const maxPages = window.innerWidth < 640 ? 3 : 5;
                    const pages = [];

                    if (totalPages <= maxPages) {
                      // Show all pages if total is less than max
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      // Calculate which pages to show
                      const startPage = Math.max(
                        1,
                        currentPage - Math.floor(maxPages / 2)
                      );
                      const endPage = Math.min(
                        totalPages,
                        startPage + maxPages - 1
                      );

                      // Adjust start if we're at the end
                      const adjustedStart =
                        endPage === totalPages
                          ? Math.max(1, endPage - maxPages + 1)
                          : startPage;

                      for (let i = adjustedStart; i <= endPage; i++) {
                        pages.push(i);
                      }
                    }

                    return pages.map(pageNum => (
                      <Button
                        key={pageNum}
                        variant={
                          currentPage === pageNum ? 'default' : 'outline'
                        }
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className="w-7 h-7 sm:w-8 sm:h-8 p-0 text-xs sm:text-sm"
                      >
                        {pageNum}
                      </Button>
                    ));
                  })()}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm"
                >
                  {isRTL ? (
                    <>
                      <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      <span className="hidden sm:inline">
                        {t('templates.table.next')}
                      </span>
                      <span className="sm:hidden">{t('templates.table.next')}</span>
                    </>
                  ) : (
                    <>
                      <span className="hidden sm:inline">
                        {t('templates.table.next')}
                      </span>
                      <span className="sm:hidden">{t('templates.table.next')}</span>
                      <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <AlertDialog
        open={!!standardToDelete}
        onOpenChange={open => !open && setStandardToDelete(null)}
      >
        <AlertDialogContent className={cn(isRTL && 'rtl')}>
          <AlertDialogHeader className={cn(isRTL && 'text-right')}>
            <AlertDialogTitle>
              {t('templates.table.deleteConfirm.title')}
            </AlertDialogTitle>
            <AlertDialogDescription className={cn(isRTL && 'text-right')}>
              {t('templates.table.deleteConfirm.description')}
              {standardToDelete && (
                <div
                  className={cn(
                    'mt-2 p-2 bg-muted/50 rounded text-sm',
                    isRTL && 'text-right'
                  )}
                >
                  <strong>{t('templates.table.standard')}:</strong>{' '}
                  {standardToDelete.text.substring(0, 100)}
                  {standardToDelete.text.length > 100 ? '...' : ''}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={cn(isRTL && 'flex-row-reverse')}>
            <AlertDialogCancel onClick={() => setStandardToDelete(null)}>
              {t('templates.table.deleteConfirm.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (standardToDelete && onDeleteStandard) {
                  onDeleteStandard(standardToDelete.id);
                  setStandardToDelete(null);
                }
              }}
              className="bg-destructive hover:bg-destructive/90"
            >
              {t('templates.table.deleteConfirm.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
